import { yupResolver } from '@hookform/resolvers/yup';
import { Auth } from 'aws-amplify';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { AButton } from 'components/atoms';
import { ModalAlert } from 'components/molecules';
import AlertMessage from 'components/molecules/AlertMessage/AlertMessage';
import { ProfileCircleCard } from 'components/molecules/ProfileCircleCard/ProfileCircleCard.components';
import { TabPanel, TabList, Tab } from 'components/molecules/TabPanel/TabPanel';
import useGetName from 'hooks/useGetName';
import { useTranslation } from 'hooks/useTranslation';
import { useUpdateProfileDataMutation } from 'services/talento';
import {
  useCompleteUserProfileProgressMutation,
  useGetUserProfileProgressQuery,
} from 'services/talentoAPI/tracking';
import { selectUserInfo } from 'store';
import { useAppSelector } from 'store/hooks';
import { selectTranslations } from 'store/translation/translation.slice';
import { EditButtonPen } from 'utils/assets';
import { ALERT_TEXT } from 'utils/constants/textChangePassword';
import {
  IProfileForm,
  IModal,
  formTypes,
  IChangePassword,
  UserProfileStatus,
} from 'utils/interfaces';
import { changePasswordFormSchema, getProfileFormSchema } from 'utils/schemas';

import { ContainerLoader } from './ContainerLoader/ContainerLoader.component';
import { useProfileFormContext } from './hooks/useProfileFormContext';
import { PersonalInfo } from './PersonalInfo/PersonalInfo.component';
import { ProfileChangePassword } from './ProfileChangePassword/ProfileChangePassword.component';
import { TabContactProfile } from './TabContactProfile/TabContactProfile.component';
import { TabEducation } from './TabEducation/TabEducation.component';
import { TabExperience } from './TabExperience/TabExperience.component';

import './ProfileForm.component.scss';
import { ExternalCodesProvider } from 'utils/helpers/ExternalCodesContext';
interface IProps {
  isDisabled: boolean;
  changeState: () => void;
  setMarginForCalendar: (state: number) => void;
  setIsDisabled: (value: boolean) => void;
  initialTab?: number;
}

export const ProfileForm = ({
  isDisabled,
  setIsDisabled,
  changeState,
  setMarginForCalendar,
  initialTab = 0,
}: IProps) => {
  const {
    currentTab,
    setCurrentTab,
    formValues,
    setFormValues,
    isEditing,
    setIsEditing,
    isLoadingProfile,
    refetchProfile,
    isFetchingProfile,
  } = useProfileFormContext();
  const { firstLastNameComplete, firstNameComplete, firstLetterName, fullName, preferredName, firstLetterSecondName, lastNameInitial } =
    useGetName();
  const { translations, isLoading } = useAppSelector(selectTranslations);
  const { email } = useAppSelector(selectUserInfo);
  const { t } = useTranslation(translations, isLoading);

  const { data: profileProgress, isFetching: isFetchingProfileProgress } =
    useGetUserProfileProgressQuery(email);

  const profileFormMethods = useForm<IProfileForm>({
    mode: 'onChange',
    resolver: yupResolver(getProfileFormSchema()),
  });

  const {
    handleSubmit: handleSubmitProfileInfo,
    setValue: setProfileInfoValue,
    getValues: getProfileInfoValues,
    reset: resetProfileInfoForm,
    trigger: triggerProfileInfoForm,
    formState: { errors: profileInfoErrors, isValid: isValidProfileInfo },
  } = profileFormMethods;

  const passwordChangeMethods = useForm<IChangePassword>({
    mode: 'all',
    resolver: yupResolver(changePasswordFormSchema),
  });
  const {
    handleSubmit: handleSubmitPassword,
    getValues: getPasswordValues,
    resetField: resetPasswordFields,
    setError: setPasswordError,
    formState: { errors: passwordFieldsErrors },
  } = passwordChangeMethods;

  const [showAlert, setShowAlert] = useState<boolean>(true);
  const [modal, setModal] = useState<IModal>({
    message: '',
    type: 'success',
    isOpen: false,
    handleClick: () => { },
  });
  const [isValidPassword, setIsValidPassword] = useState<boolean>(false);
  const [passwordValidationValue, setPasswordValidationValue] = useState<boolean | null>(null);
  const [validationPasswordError, setValidationPasswordError] = useState<string>('');
  const [isSubmitingForm, setIsSubmitingForm] = useState<boolean>(false);

  const [completeUserProfileProgress] = useCompleteUserProfileProgressMutation();

  const [
    updateProfileData,
    { isLoading: isLoadingUpdate, isSuccess: isSuccessUpdate, error: errorUpdate },
  ] = useUpdateProfileDataMutation();

  const personalInfoFields: (keyof IProfileForm)[] = [
    'numberId',
    'bornDate',
    'preferredName',
    'firstName',
    'lastName',
    'bloodType',
    'idType',
  ];
  const passwordChangeFields: (keyof IChangePassword)[] = [
    'currentPassword',
    'newPassword',
    'confirmNewPassword',
  ];

  const educationInfoFields: (keyof IProfileForm)[] = ['educationLevel', 'institution', 'title'];

  const experienceInfoFields: (keyof IProfileForm)[] = [
    'jobtitle',
    'company',
    'job_start_date',
    'job_end_date',
    'currentSalaryCurrency',
    'currentSalary',
    'expectedSalaryCurrencyDropdown',
    'expectedSalaryNumeric',
  ];

  const contactInfoFields: (keyof IProfileForm)[] = [
    'email',
    'phoneCode',
    'phoneNumber',
    'country',
    'state',
    'city',
    'direction',
  ];

  const personalInfoHasErrors = personalInfoFields.some((field) => !!profileInfoErrors[field]);
  const personalInfoState = personalInfoHasErrors ? 'error' : 'none';

  const educationInfoHasErrors = educationInfoFields.some((field) => !!profileInfoErrors[field]);
  const educationInfoState = educationInfoHasErrors ? 'error' : 'none';

  const experienceInfoHasErrors = experienceInfoFields.some((field) => !!profileInfoErrors[field]);
  const experienceInfoState = experienceInfoHasErrors ? 'error' : 'none';

  const contactInfoHasErrors = contactInfoFields.some((field) => !!profileInfoErrors[field]);
  const contactInfoState = contactInfoHasErrors ? 'error' : 'none';

  const passwordChangeHasError =
    passwordChangeFields.some((field) => !!passwordFieldsErrors[field]) ||
    !!validationPasswordError;
  const passwordChangeState = passwordChangeHasError ? 'error' : 'none';

  const changeData = async ({
    name,
    value,
  }: {
    name: formTypes;
    value?: number | string | boolean;
  }) => {
    setFormValues((prevState) => ({
      ...prevState,
      [name]: value || '',
    }));
    const formValue = value !== undefined ? value : '';
    setProfileInfoValue(name, formValue, { shouldValidate: true });
  };

  const saveProfileInfoData = async () => {
    let combinedValues = { firstName: '', lastName: '' };
    if (typeof window !== 'undefined' && (window as any).combineNames) {
      combinedValues = (window as any).combineNames();
    }

    const {
      email,
      phoneNumber,
      phoneCode,
      country,
      state,
      city,
      direction,
      bloodType,
      idType,
      numberId,
      bornDate,
      educationLevel,
      institution,
      title,
      jobtitle,
      company,
      job_start_date,
      job_end_date,
      currentSalaryCurrency,
      currentSalary,
      expectedSalaryCurrencyDropdown,
      expectedSalaryNumeric,
      hasExperience,
      preferredName,
      firstName,
      lastName,
      secondName,
      secondLastName,
    } = getProfileInfoValues();
    const extractPhoneCodeNumber = (phoneCode: string): string => {
      if (!phoneCode) return '';
      return phoneCode.replace('+', '');
    };
    const finalData = {
      email,
      phoneNumber: phoneNumber || '',
      phoneCode: extractPhoneCodeNumber(String(phoneCode || '')),
      country,
      state,
      city,
      direction,
      bloodType,
      idType,
      numberId,
      bornDate,
      educationLevel,
      institution,
      title,
      jobtitle,
      company,
      job_start_date,
      job_end_date,
      currentSalaryCurrency,
      currentSalary,
      expectedSalaryCurrencyDropdown,
      expectedSalaryNumeric,
      hasExperience: !hasExperience,
      preferredName,
      firstName: combinedValues.firstName,
      lastName: combinedValues.lastName,
      secondName: secondName || '',
      secondLastName: secondLastName || '',
    };

    return await updateProfileData(finalData);
  };

  const setViewMode = () => {
    setIsDisabled(true);
    setIsEditing(false);
  };

  const setEditingMode = () => {
    setIsDisabled(false);
    setIsEditing(true);
  };

  const asyncChangePassword = async () => {
    const { currentPassword, newPassword } = getPasswordValues();

    const authUser = await Auth.currentAuthenticatedUser();

    try {
      await Auth.changePassword(authUser, currentPassword, newPassword);

      resetPasswordFields('confirmNewPassword');
      resetPasswordFields('currentPassword');
      resetPasswordFields('newPassword');
      setValidationPasswordError('');

      return true;
    } catch (e: any) {
      const { name } = e;

      if (name?.includes('NotAuthorizedException')) {
        setPasswordError('currentPassword', {
          type: 'custom',
          message: 'La contraseña actual es incorrecta',
        });
      }

      if (name?.includes('LimitExceededException')) {
        setValidationPasswordError(t('limitExceededException'));
      }

      return false;
    }
  };

  const savePasswordData = async () => {
    const { currentPassword, newPassword } = getPasswordValues();

    if (currentPassword === newPassword) {
      setValidationPasswordError('Tu contraseña nueva no puede ser igual a la actual');

      return false;
    } else {
      return await asyncChangePassword();
    }
  };

  const handleSaveOrEdit = async () => {
    if (isEditing) {
      const isValid = await triggerProfileInfoForm();
      console.log('Pre-save validation:', isValid);
      setIsSubmitingForm(true);
    } else {
      setEditingMode();
    }
  };

  const handleSaveData = async () => {
    if (typeof window !== 'undefined' && (window as any).combineNames) {
      (window as any).combineNames();
    }

    const isValid = await triggerProfileInfoForm();

    console.log('Form validation result:', isValid);
    console.log('Current form errors:', profileInfoErrors);
    console.log('Current form values:', getProfileInfoValues());

    const { firstName, secondName } = getProfileInfoValues();

    if (!secondName) {
      changeData({ name: 'preferredName', value: firstName });
    }

    if (!isValid) {
      setIsSubmitingForm(false);
      openErrorModal();
      return;
    }

    try {
      await saveProfileInfoData();
      await completeUserProfileProgress({ email: getProfileInfoValues().email }).unwrap();

      await refetchProfile();
      setCurrentTab(0);
      setViewMode();
      openSuccessfulModal();
    } catch (error) {
      setEditingMode();
      openErrorModal();
    } finally {
      setIsSubmitingForm(false);
    }
  };

  const scrollToTab = (id: number | string, element: HTMLDivElement | null) => {
    if (element) {
      setCurrentTab(id as number);
      element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  };

  const openErrorModal = () => {
    setModal({
      message: 'Ha ocurrido algo, revisa el formulario e inténtalo de nuevo.',
      type: 'error',
      isOpen: true,
    });
  };

  const openSuccessfulModal = () => {
    setModal({ message: '¡Cambios guardados!', type: 'success', isOpen: true });
  };

  const successfulyDataUpdating = async (passwordValidation: boolean) => {
    if (passwordValidation) {
      await refetchProfile();
      setCurrentTab(0);
      setViewMode();
      openSuccessfulModal();
    }
    if (!passwordValidation) {
      setEditingMode();
      openErrorModal();
    }
    setPasswordValidationValue(null);
    setIsSubmitingForm(false);
  };

  const checkProfileStatus = () => {
    if (profileProgress?.data?.state === UserProfileStatus.COMPLETED) {
      setViewMode();
    }
  };

  useEffect(() => {
    if (initialTab !== undefined && initialTab !== currentTab) {
      setCurrentTab(initialTab);
    }
  }, [initialTab]);

  useEffect(() => {
    setShowAlert(true);
  }, []);

  useEffect(() => {
    if (isSubmitingForm) {
      handleSaveData();
    }
  }, [isSubmitingForm]);

  useEffect(() => {
    let validation: boolean;

    if (!isLoadingUpdate && passwordValidationValue !== null) {
      validation = passwordValidationValue;

      if (errorUpdate) {
        validation = false;
      }

      if (!errorUpdate && isSuccessUpdate) {
        changeState();
      }

      successfulyDataUpdating(validation);
    }
  }, [isLoadingUpdate, isSuccessUpdate, errorUpdate, passwordValidationValue]);

  useEffect(() => {
    if (!isFetchingProfile) {
      checkProfileStatus();
    }
  }, [profileProgress, isFetchingProfile]);

  useEffect(() => {
    triggerProfileInfoForm();
    return () => {
      resetProfileInfoForm();
    };
  }, []);

  useEffect(() => {
    const alertClosed = localStorage.getItem('alertClosed') === 'true';
    setShowAlert(!alertClosed);
  }, []);

  if (
    isLoadingProfile ||
    isLoadingUpdate ||
    isFetchingProfile ||
    isFetchingProfileProgress ||
    passwordValidationValue !== null
  ) {
    return <ContainerLoader />;
  }

  return (
    <>
      <div className='container-alert'>
        {showAlert && (
          <AlertMessage
            message={ALERT_TEXT}
            onClose={() => {
              setShowAlert(false);
              localStorage.setItem('alertClosed', 'true');
            }}
          />
        )}
      </div>
      <div className='container-form' data-testid='form'>
        <ModalAlert
          alertType={modal.type}
          alertText={modal.message}
          alertTitle={modal.title ? modal.title : undefined}
          isOpened={modal.isOpen}
          confirmButton={true}
          toggleModal={(isOpen: boolean) => setModal((state) => ({ ...state, isOpen }))}
          handleClick={modal.handleClick}
        />
        <section className='sectionHeader'>
          <ProfileCircleCard
            name={
              (formValues.preferredName || formValues.firstName || firstNameComplete)?.charAt(0) ||
              firstLetterName
            }
            secondName={
              (formValues.lastName || firstLastNameComplete)?.charAt(0) || firstLetterSecondName
            }
          />
          <p className='sectionHeader__name'>
            {formValues.preferredName || formValues.firstName || preferredName || firstNameComplete}{' '}
            {formValues.lastName || firstLastNameComplete}
          </p>
          <div className='sectionHeader__text'>
            <h2>Actualiza tu perfil con tu información </h2>
          </div>
          <div className='sectionHeader__text__second'>
            <p>Completa tus datos para que podamos mantener tu información al día.</p>
          </div>
          <AButton
            tag='a'
            className={`btn-profile button-primary`}
            disabled={isEditing ? !isValidProfileInfo || isSubmitingForm : false}
            handleClick={handleSaveOrEdit}
          >
            <img
              src={isEditing ? '' : EditButtonPen}
              alt={isEditing ? '' : 'Editar'}
              className={` ${!isEditing ? 'sectionHeader__imgPen' : ''}`}
            />
            {isEditing ? 'Guardar' : 'Editar'}
          </AButton>
        </section>
        <div className='profile-tabs'>
          <TabPanel initialTab={currentTab}>
            <TabList>
              <Tab
                id={0}
                onClickExtra={scrollToTab}
                state={personalInfoHasErrors ? 'error' : 'success'}
              >
                Información personal
              </Tab>

              <Tab
                id={1}
                onClickExtra={scrollToTab}
                state={contactInfoHasErrors ? 'error' : 'success'}
              >
                Contacto
              </Tab>

              <Tab
                id={2}
                onClickExtra={scrollToTab}
                state={educationInfoHasErrors ? 'error' : 'success'}
              >
                Educación
              </Tab>

              <Tab
                id={3}
                onClickExtra={scrollToTab}
                state={experienceInfoHasErrors ? 'error' : 'success'}
              >
                Experiencia laboral
              </Tab>
            </TabList>
            <div className='border-bottom'></div>

            <FormProvider {...profileFormMethods}>
              <form onSubmit={handleSubmitProfileInfo(handleSaveOrEdit)}>
                <ExternalCodesProvider>
                  <PersonalInfo
                    {...{
                      formValues,
                      isDisabled,
                      changeData,
                      setMarginForCalendar,
                      isEditing,
                    }}
                  />
                </ExternalCodesProvider>
                <ExternalCodesProvider>
                  <TabContactProfile
                    {...{
                      formValues,
                      isDisabled,
                      changeData,
                      isEditing,
                    }}
                  />
                </ExternalCodesProvider>
                <TabEducation
                  {...{
                    formValues,
                    isDisabled,
                    changeData,
                    isEditing,
                  }}
                />
                <TabExperience
                  profileProgress={profileProgress?.data?.state}
                  {...{
                    isDisabled,
                    changeData,
                    setMarginForCalendar,
                  }}
                />
              </form>
            </FormProvider>

          </TabPanel>
        </div>
      </div>
    </>
  );
};
